import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useOceanData } from '../hooks/useOceanData';
import { useAnimationControl } from '../hooks/useAnimationControl';
import { useEnvironmentalData } from '../hooks/useEnvironmentalData';
import LoadingScreen from '../components/common/LoadingScreen';
import ErrorScreen from '../components/common/ErrorScreen';
import { getRowTimeMs, frameIndexForStoryTime, VESSEL_MAP_PLAYBACK_VIEW_MODES } from '../utils/storyTimeUtils';

const OceanDataContext = createContext(null);

/** Views where vessel playback time (MapContainer) drives the clock; frame index follows. */
const VESSEL_TIMELINE_MODES = VESSEL_MAP_PLAYBACK_VIEW_MODES;

export const useOcean = () => {
  const context = useContext(OceanDataContext);
  if (context === null) {
    throw new Error('useOcean must be used within an OceanDataProvider');
  }
  return context;
};

export const OceanDataProvider = ({ children }) => {
  const oceanData = useOceanData();

  const {
    storySeries,
    totalFrames,
    setVesselPlaybackTime,
    vesselPlaybackTime,
    setCurrentDate,
    viewMode,
    selectedDepth,
    notifyPlaybackReset
  } = oceanData;

  const [isPlaying, setIsPlaying] = useState(false);
  const playAnimation = useCallback(() => setIsPlaying(true), []);
  const pauseAnimation = useCallback(() => setIsPlaying(false), []);
  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  /** When Play resumes at the first story frame, clear exercise comms so the log matches a fresh run. */
  const prevIsPlayingRef = useRef(isPlaying);
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current;
    prevIsPlayingRef.current = isPlaying;
    if (!isPlaying || wasPlaying) return;
    if (!VESSEL_TIMELINE_MODES.has(viewMode)) return;
    if (vesselPlaybackTime == null || storySeries.length === 0) return;
    const idx = frameIndexForStoryTime(vesselPlaybackTime, storySeries);
    if (idx <= 0) notifyPlaybackReset();
  }, [isPlaying, viewMode, vesselPlaybackTime, storySeries, notifyPlaybackReset]);

  const frameAutoAdvance = !VESSEL_TIMELINE_MODES.has(viewMode);

  const animationControl = useAnimationControl(
    totalFrames,
    isPlaying && frameAutoAdvance,
    { pauseAnimation, togglePlay }
  );

  const {
    jumpToFrame,
    handleReset: animationHandleReset,
    currentFrame
  } = animationControl;

  const environmentalData = useEnvironmentalData(
    storySeries,
    currentFrame,
    selectedDepth ?? 0
  );

  const handleFrameChange = useCallback((frameIndex) => {
    jumpToFrame(frameIndex);
    const row = storySeries[frameIndex];
    if (row) {
      const ms = getRowTimeMs(row);
      if (!Number.isNaN(ms)) {
        setVesselPlaybackTime(ms);
        setCurrentDate(new Date(ms));
      }
    }
  }, [jumpToFrame, storySeries, setVesselPlaybackTime, setCurrentDate]);

  const handleReset = useCallback(() => {
    animationHandleReset();
    notifyPlaybackReset();
    if (storySeries.length > 0) {
      const ms = getRowTimeMs(storySeries[0]);
      if (!Number.isNaN(ms)) setVesselPlaybackTime(ms);
    } else {
      setVesselPlaybackTime(null);
    }
  }, [animationHandleReset, notifyPlaybackReset, storySeries, setVesselPlaybackTime]);

  // Ocean / non-vessel views: advancing frame is the source of truth for story clock.
  useEffect(() => {
    if (VESSEL_TIMELINE_MODES.has(viewMode)) return;
    const row = storySeries[currentFrame];
    if (row == null) return;
    const ms = getRowTimeMs(row);
    if (Number.isNaN(ms)) return;
    setVesselPlaybackTime(ms);
  }, [currentFrame, viewMode, storySeries, setVesselPlaybackTime]);

  // Vessel views: MapContainer advances vesselPlaybackTime; keep scrubber frame aligned.
  useEffect(() => {
    if (!VESSEL_TIMELINE_MODES.has(viewMode)) return;
    if (vesselPlaybackTime == null || storySeries.length === 0) return;
    const idx = frameIndexForStoryTime(vesselPlaybackTime, storySeries);
    if (idx !== currentFrame) {
      jumpToFrame(idx);
    }
  }, [vesselPlaybackTime, viewMode, storySeries, currentFrame, jumpToFrame]);

  const handleDateTimeChange = useCallback(() => null, []);

  const value = {
    ...oceanData,
    ...animationControl,
    ...environmentalData,
    handleReset,
    handleFrameChange,
    handleDateTimeChange,
    handlePlayToggle: togglePlay,
    isPlaying,
    playAnimation,
    pauseAnimation,
    togglePlay
  };

  if (value.isLoading && !value.dataLoaded) {
    return <LoadingScreen />;
  }

  if (value.hasError) {
    return <ErrorScreen message={value.errorMessage} />;
  }

  return (
    <OceanDataContext.Provider value={value}>
      {children}
    </OceanDataContext.Provider>
  );
};
